import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { createContext, useContext, useState } from "react";

const AlertContext = createContext();

const alertStyles = {
  success: {
    bg: "bg-green-50 dark:bg-green-500/10 dark:outline dark:outline-green-500/20",
    title: "text-green-800 dark:text-green-200",
    msg: "text-green-700 dark:text-green-200/85",
    icon: <CheckCircleIcon aria-hidden="true" className="size-5 text-green-400" />,
    close: "bg-green-50 text-green-500 hover:bg-green-100 focus-visible:ring-green-600 focus-visible:ring-offset-green-50 dark:text-green-400 dark:hover:bg-green-500/10 dark:focus-visible:ring-green-500 dark:focus-visible:ring-offset-green-900"
  },
  warning: {
    bg: "bg-yellow-50 dark:bg-yellow-500/10 dark:outline dark:outline-yellow-500/15",
    title: "text-yellow-800 dark:text-yellow-100",
    msg: "text-yellow-700 dark:text-yellow-100/80",
    icon: <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-yellow-400 dark:text-yellow-300" />,
    close: "bg-yellow-50 text-yellow-500 hover:bg-yellow-100 focus-visible:ring-yellow-600 focus-visible:ring-offset-yellow-50 dark:text-yellow-400 dark:hover:bg-yellow-500/10 dark:focus-visible:ring-yellow-500 dark:focus-visible:ring-offset-yellow-900"
  },
  error: {
    bg: "bg-red-50 dark:bg-red-500/15 dark:outline dark:outline-red-500/25",
    title: "text-red-800 dark:text-red-200",
    msg: "text-red-700 dark:text-red-200/80",
    icon: <XCircleIcon aria-hidden="true" className="size-5 text-red-400" />,
    close: "bg-red-50 text-red-500 hover:bg-red-100 focus-visible:ring-red-600 focus-visible:ring-offset-red-50 dark:text-red-400 dark:hover:bg-red-500/10 dark:focus-visible:ring-red-500 dark:focus-visible:ring-offset-red-900"
  },
};

export function AlertProvider({ children }) {
  const [alert, setAlert] = useState(null);
    const [closing, setClosing] = useState(false);
  const showAlert = (message, title = "",type = "success", timeout = 3000) => {
    setClosing(false);
    setAlert({ message, title ,type });
    setTimeout(() => {
      setClosing(true);
      setTimeout(() => setAlert(null), 400); // wait for animation
    }, timeout);
  };
  const closeAlert = () => {
    setClosing(true);
    setTimeout(() => setAlert(null), 400); // wait for animation
  };

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      {alert && (
        <div className={`fixed top-6 sm:right-6 z-50 rounded-md p-4 min-w-full max-w-full sm:min-w-[410px] sm:max-w-[450px] ${closing 
      ? "[animation:slide-out-right_0.4s_ease-in]" 
      : "[animation:slide-in-right_0.4s_ease-out]"
    } ${alertStyles[alert.type].bg}`}>
          <div className="flex">
            <div className="shrink-0">{alertStyles[alert.type].icon}</div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${alertStyles[alert.type].title}`}>{alert.title}</h3>
              <div className={`text-sm ${alertStyles[alert.type].msg} ${alert.title ? "mt-2" : ""}`}>
                <p>{alert.message}</p>
              </div>
            </div>
            <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                    <button
                    type="button"
                    className={`inline-flex rounded-md p-1.5 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden dark:bg-transparent dark:focus-visible:ring-offset-1 ${alertStyles[alert.type].close}`}
                    >
                    <span className="sr-only">Dismiss</span>
                    <XMarkIcon aria-hidden="true" className="size-5" onClick={() => closeAlert()} />
                    </button>
                </div>
            </div>
          </div>
        </div>
      )}
    </AlertContext.Provider>
  );
}

export function useAlert() {
  return useContext(AlertContext);
}